import axios from 'axios';

// Instancia de axios para la API de Strapi
const strapiApi = axios.create({
  baseURL: 'https://staging-strapi.orsai.org/api',
  timeout: 30000,
});

// Sistema de cache mejorado con localStorage
class CuentosCache {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutos por defecto
    this.storageKey = 'cuentos_cache_v1'; // Versión para invalidar cache antiguo
    this.maxStorageSize = 50 * 1024 * 1024; // 50MB límite aproximado
  }

  // Genera una clave única para el cache basada en el endpoint y parámetros
  generateKey(endpoint, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}:${JSON.stringify(params[key])}`)
      .join('|');
    return `${endpoint}|${sortedParams}`;
  }

  // Obtiene datos del localStorage
  async getFromStorage(key) {
    try {
      const storageKey = `${this.storageKey}_${this.hashKey(key)}`;
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;
      
      const { data, expiresAt } = JSON.parse(stored);
      if (Date.now() > expiresAt) {
        localStorage.removeItem(storageKey);
        return null;
      }
      
      console.log('💾 Storage hit:', key);
      return data;
    } catch (error) {
      console.warn('⚠️ Error reading from localStorage:', error);
      return null;
    }
  }

  // Guarda datos en localStorage
  async saveToStorage(key, data, ttl) {
    try {
      const storageKey = `${this.storageKey}_${this.hashKey(key)}`;
      const expiresAt = Date.now() + ttl;
      const toStore = { data, expiresAt };
      
      // Verificar tamaño antes de guardar
      const serialized = JSON.stringify(toStore);
      if (serialized.length > 1024 * 1024) { // 1MB por item
        console.warn('⚠️ Item too large for localStorage:', key);
        return;
      }
      
      localStorage.setItem(storageKey, serialized);
      console.log('💾 Storage saved:', key);
      
      // Limpiar storage si está cerca del límite
      this.cleanupStorageIfNeeded();
    } catch (error) {
      console.warn('⚠️ Error saving to localStorage:', error);
      // Limpiar localStorage si está lleno
      this.cleanupStorage();
    }
  }

  // Hash simple para las claves de localStorage
  hashKey(key) {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      const char = key.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  // Limpia localStorage si está cerca del límite
  cleanupStorageIfNeeded() {
    try {
      let totalSize = 0;
      const keys = [];
      
      // Calcular tamaño total
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storageKey)) {
          const value = localStorage.getItem(key);
          totalSize += (key.length + value.length) * 2; // UTF-16 chars
          keys.push({ key, size: value.length, timestamp: this.getStorageTimestamp(key) });
        }
      }
      
      // Si supera el 80% del límite, limpiar items más antiguos
      if (totalSize > this.maxStorageSize * 0.8) {
        console.log('🧹 Cleaning localStorage, current size:', totalSize);
        this.cleanupStorage();
      }
    } catch (error) {
      console.warn('⚠️ Error checking storage size:', error);
    }
  }

  // Obtiene timestamp de un item en storage
  getStorageTimestamp(key) {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const { expiresAt } = JSON.parse(stored);
        return expiresAt;
      }
    } catch (error) {
      console.warn('⚠️ Error getting storage timestamp:', error);
    }
    return 0;
  }

  // Limpia localStorage eliminando items más antiguos
  cleanupStorage() {
    try {
      const keys = [];
      
      // Recolectar todas las claves del cache
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storageKey)) {
          const timestamp = this.getStorageTimestamp(key);
          keys.push({ key, timestamp });
        }
      }
      
      // Ordenar por timestamp (más antiguos primero)
      keys.sort((a, b) => a.timestamp - b.timestamp);
      
      // Eliminar el 50% más antiguo
      const toDelete = Math.floor(keys.length * 0.5);
      for (let i = 0; i < toDelete; i++) {
        localStorage.removeItem(keys[i].key);
      }
      
      console.log('🧹 Cleaned', toDelete, 'items from localStorage');
    } catch (error) {
      console.warn('⚠️ Error cleaning localStorage:', error);
    }
  }

  // Obtiene datos del cache (modificado para usar localStorage)
  async get(key) {
    // 1. Intentar cache en memoria (más rápido)
    const memoryCached = this.cache.get(key);
    if (memoryCached && Date.now() <= memoryCached.expiresAt) {
      console.log('📦 Memory cache hit:', key);
      return memoryCached.data;
    }

    // 2. Intentar localStorage
    const storageCached = await this.getFromStorage(key);
    if (storageCached) {
      // Restaurar en cache de memoria
      this.set(key, storageCached, this.defaultTTL);
      return storageCached;
    }

    return null;
  }

  // Guarda datos en el cache (modificado para usar localStorage)
  async set(key, data, ttl = this.defaultTTL) {
    const expiresAt = Date.now() + ttl;
    
    // Guardar en memoria
    this.cache.set(key, { data, expiresAt });
    
    // Guardar en localStorage solo para cuentos individuales y datos importantes
    if (this.shouldStoreInLocalStorage(key)) {
      await this.saveToStorage(key, data, ttl);
    }
    
    console.log('💾 Cache set:', key, 'expires at:', new Date(expiresAt).toLocaleTimeString());
  }

  // Determina si un item debe guardarse en localStorage
  shouldStoreInLocalStorage(key) {
    // Guardar cuentos individuales, valoraciones y estadísticas
    return key.includes('/cuentos/document/') || 
           key.includes('/cuentos/') && !key.includes('with-versions') ||
           key.includes('/valoraciones/') ||
           key.includes('/estadisticas');
  }

  // Invalida cache por patrón
  async invalidate(pattern) {
    const keysToDelete = [];
    
    // Invalidar cache en memoria
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      console.log('🗑️ Memory cache invalidated:', key);
    });
    
    // Invalidar localStorage
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storageKey)) {
          const originalKey = this.getOriginalKeyFromStorage(key);
          if (originalKey && originalKey.includes(pattern)) {
            localStorage.removeItem(key);
            console.log('🗑️ Storage cache invalidated:', originalKey);
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Error invalidating storage cache:', error);
    }
  }

  // Obtiene la clave original desde la clave de storage
  getOriginalKeyFromStorage(storageKey) {
    // Esta es una implementación simplificada
    // En un caso real, podrías mantener un mapeo de claves
    return storageKey.replace(`${this.storageKey}_`, '');
  }

  // Limpia todo el cache
  async clear() {
    this.cache.clear();
    
    // Limpiar localStorage
    try {
      const keysToDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storageKey)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('⚠️ Error clearing localStorage:', error);
    }
    
    console.log('🧹 Cache cleared (memory + localStorage)');
  }

  // Obtiene estadísticas del cache
  getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    let storageEntries = 0;

    // Estadísticas de memoria
    for (const [key, cached] of this.cache.entries()) {
      if (now > cached.expiresAt) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    }

    // Estadísticas de localStorage
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storageKey)) {
          storageEntries++;
        }
      }
    } catch (error) {
      console.warn('⚠️ Error getting storage stats:', error);
    }

    return {
      memory: {
        total: this.cache.size,
        valid: validEntries,
        expired: expiredEntries
      },
      storage: {
        total: storageEntries
      }
    };
  }
}

// Instancia global del cache
const cuentosCache = new CuentosCache();

// Función helper para hacer requests con cache (modificada para ser async)
const cachedRequest = async (endpoint, params = {}, ttl = null) => {
  const cacheKey = cuentosCache.generateKey(endpoint, params);
  
  // Intentar obtener del cache primero (ahora async)
  const cachedData = await cuentosCache.get(cacheKey);
  if (cachedData) {
    return cachedData;
  }

  // Si no está en cache, hacer la request
  const response = await strapiApi.get(endpoint, { params });
  
  // Guardar en cache (ahora async)
  await cuentosCache.set(cacheKey, response.data, ttl);
  
  return response.data;
};

/**
 * Obtiene cuentos con versiones desde la API de Strapi
 * @param {number} page - Número de página
 * @param {string} version - Versión de los cuentos (ej: 'V4')
 * @param {Object} filters - Filtros a aplicar
 * @param {number} pageSize - Tamaño de página (por defecto 24)
 * @returns {Promise} - Promise con los datos de los cuentos
 */
export const getCuentosWithVersions = async (page = 1, version = 'V4', filters = {}, pageSize = 24) => {
  try {
    // Extraer pageSize de los filtros si existe, sino usar el proporcionado
    const { pageSize: filterPageSize, ...otherFilters } = filters;
    // Asegurar que pageSize sea un número
    const finalPageSize = parseInt(filterPageSize || pageSize);

    const params = {
      version,
      'pagination[page]': page,
      'pagination[pageSize]': finalPageSize,
      sort: 'updatedAt:desc',
      ...otherFilters
    };

    // Asegurar que pageSize se envíe explícitamente como parámetro de query
    params.pageSize = finalPageSize;

    console.log('🔍 Parámetros enviados al servicio:', {
      page,
      pageSize,
      finalPageSize,
      version,
      filters,
      params,
      url: `${strapiApi.defaults.baseURL}/cuentos/with-versions?${new URLSearchParams(params).toString()}`
    });

    // Usar cache con TTL más corto para listas paginadas (2 minutos)
    const data = await cachedRequest('/cuentos/with-versions', params, 2 * 60 * 1000);

    console.log(`📚 Cuentos obtenidos - Página ${page}, PageSize: ${finalPageSize}, Total recibidos: ${data?.data?.length || 0}:`, {
      totalEnPagina: data?.data?.length || 0,
      paginacion: data?.meta?.pagination,
      filtrosEnviados: filters,
      pageSizeEnviado: finalPageSize,
      pageSizeDevuelto: data?.meta?.pagination?.pageSize,
      coinciden: finalPageSize === data?.meta?.pagination?.pageSize
    });

    return data;
  } catch (error) {
    console.error('❌ Error al obtener cuentos:', error);
    throw error;
  }
};

/**
 * Obtiene las opciones de filtros disponibles
 * @param {string} version - Versión de los cuentos
 * @returns {Promise} - Promise con las opciones de filtros
 */
export const getCuentosFilterOptions = async (version = 'V4') => {
  try {
    // Cache más largo para opciones de filtros (10 minutos)
    const data = await cachedRequest('/alumnos/filters', { version }, 10 * 60 * 1000);
    
    console.log('🔍 Opciones de filtros obtenidas:', data);
    return data;
  } catch (error) {
    console.error('❌ Error al obtener opciones de filtros:', error);
    throw error;
  }
};

/**
 * Transforma los datos de la API al formato esperado por el componente
 * @param {Array} cuentosData - Datos de cuentos de la API
 * @returns {Array} - Array de cuentos transformados
 */
export const transformCuentosData = (cuentosData) => {
  return cuentosData.map(cuento => ({
    id: cuento.id,
    title: cuento.titulo || 'Sin título',
    author: cuento.alumno ? `${cuento.alumno.nombre} ${cuento.alumno.apellido}` : 'Anónimo',
    description: cuento.contenido?.resumen || cuento.resumen || 'Sin descripción disponible',
    image: cuento.cover || '/src/assets/images/Sonoro/anecdota.png',
    audio: cuento.audio_v2,
    genre: cuento.taller?.genero || 'Anécdota',
    tags: (cuento.tags || []).map(tag => tag.charAt(0).toUpperCase() + tag.slice(1)),
    documentId: cuento.documentId,
    idUsuario: cuento.idUsuario, // ID del usuario autor directamente del cuento
    createdAt: cuento.createdAt,
    publishedAt: cuento.publishedAt,
    // Datos del alumno para filtros
    alumno: cuento.alumno ? {
      id: cuento.alumno.id,
      idUsuario: cuento.alumno.idUsuario, // Agregar idUsuario al objeto alumno
      nombre: cuento.alumno.nombre,
      apellido: cuento.alumno.apellido,
      pais: cuento.alumno.pais,
      paisNacimiento: cuento.alumno.paisNacimiento,
      genero: cuento.alumno.genero,
      fechaNacimiento: cuento.alumno.fechaNacimiento,
      estudios: cuento.alumno.estudios,
      profesion: cuento.alumno.profesion,
      estadoCivil: cuento.alumno.estadoCivil,
      hijos: cuento.alumno.hijos,
      signo: cuento.alumno.signo,
      grupoEtario: cuento.alumno.grupoEtario
    } : null
  }));
};

/**
 * Obtiene un cuento por documentId desde la API de Strapi
 * @param {string} documentId - El documentId del cuento
 * @returns {Promise} - Promise con los datos del cuento
 */
export const getCuentoByDocumentId = async (documentId) => {
  try {
    // Cache más largo para cuentos individuales (15 minutos)
    const data = await cachedRequest(`/cuentos/document/${documentId}`, {}, 15 * 60 * 1000);
    return data;
  } catch (error) {
    console.error('❌ Error al obtener cuento por documentId:', error);
    throw error;
  }
};

/**
 * Obtiene un cuento por su ID numérico
 * @param {number} id - El ID numérico del cuento
 * @returns {Promise} - Promise con los datos del cuento
 */
export const getCuentoById = async (id) => {
  try {
    const params = {
      'filters[id][$eq]': id,
      'pagination[pageSize]': 1
    };

    // Cache más largo para cuentos individuales (15 minutos)
    const data = await cachedRequest('/cuentos', params, 15 * 60 * 1000);

    // Obtener el primer resultado de data si existe
    const cuento = data?.data?.[0];

    if (!cuento) {
      throw new Error(`No se encontró cuento con ID: ${id}`);
    }

    return cuento;
  } catch (error) {
    console.error('❌ Error al obtener cuento por ID:', error);
    throw error;
  }
};

/**
 * Obtiene las valoraciones de un cuento específico
 * @param {string} documentId - El documentId del cuento
 * @returns {Promise} - Promise con las valoraciones del cuento
 */
export const getCuentoValoraciones = async (documentId) => {
  try {
    // Cache corto para valoraciones (1 minuto) ya que pueden cambiar frecuentemente
    const data = await cachedRequest(`/valoraciones/cuento/${documentId}`, {}, 60 * 1000);
    return data;
  } catch (error) {
    console.error('❌ Error al obtener valoraciones del cuento:', error);
    throw error;
  }
};

/**
 * Envía una valoración para un cuento
 * @param {string} documentId - El documentId del cuento
 * @param {number} rating - Valoración (1-5)
 * @param {number} userId - ID del usuario
 * @param {string} userEmail - Email del usuario
 * @returns {Promise} - Promise con la respuesta del servidor
 */
export const enviarValoracion = async (documentId, rating, userId, userEmail) => {
  try {
    // Validar que se proporcionen los datos requeridos
    if (!userId || !userEmail) {
      throw new Error('Se requieren userId y userEmail para valorar cuentos');
    }

    // Validar que el rating esté en el rango correcto
    if (rating < 1 || rating > 5) {
      throw new Error('La valoración debe estar entre 1 y 5');
    }

    const response = await strapiApi.post(`/valoraciones/cuento/${documentId}`, {
      rating,
      userId,
      userEmail
    });

    // Invalidar cache relacionado con valoraciones
    await cuentosCache.invalidate('valoraciones');
    await cuentosCache.invalidate(`/valoraciones/cuento/${documentId}`);

    return response.data;
  } catch (error) {
    console.error('❌ Error al enviar valoración:', error);
    throw error;
  }
};

/**
 * Verifica si un usuario ya valoró un cuento específico
 * @param {string} documentId - El documentId del cuento
 * @param {string} userEmail - Email del usuario
 * @returns {Promise} - Promise con la valoración existente o null
 */
export const verificarValoracionUsuario = async (documentId, userEmail) => {
  try {
    // Cache corto para verificaciones de usuario (30 segundos)
    const data = await cachedRequest(`/valoraciones/cuento/${documentId}/usuario`, { userEmail }, 30 * 1000);
    return data;
  } catch (error) {
    console.error('❌ Error al verificar valoración del usuario:', error);
    return null;
  }
};

/**
 * Obtiene estadísticas de valoraciones de un cuento
 * @param {string} documentId - El documentId del cuento
 * @returns {Promise} - Promise con las estadísticas de valoraciones
 */
export const getEstadisticasValoraciones = async (documentId) => {
  try {
    // Cache medio para estadísticas (3 minutos)
    const data = await cachedRequest(`/valoraciones/cuento/${documentId}/estadisticas`, {}, 3 * 60 * 1000);
    return data;
  } catch (error) {
    console.error('❌ Error al obtener estadísticas de valoraciones:', error);
    throw error;
  }
};

/**
 * Obtiene los cuentos mejor valorados desde el endpoint específico
 * @returns {Promise} - Promise con los datos de los cuentos mejor valorados
 */
export const getCuentosMejorValorados = async () => {
  try {
    console.log('🔍 Obteniendo cuentos mejor valorados...');

    // Cache medio para rankings (5 minutos)
    const data = await cachedRequest('/cuentos/mejor-valorados', {}, 5 * 60 * 1000);

    console.log('📚 Cuentos mejor valorados obtenidos:', {
      totalRecibidos: data?.data?.length || 0,
      datos: data
    });

    return data;
  } catch (error) {
    console.error('❌ Error al obtener cuentos mejor valorados:', error);
    throw error;
  }
};

/**
 * Obtiene el cuento anterior basado en fecha de publicación
 * @param {string} currentDocumentId - El documentId del cuento actual
 * @returns {Promise} - Promise con el cuento anterior
 */
export const getCuentoAnterior = async (currentDocumentId) => {
  try {
    // Cache corto para navegación (1 minuto)
    const data = await cachedRequest(`/cuentos/adjacent/${currentDocumentId}`, { direction: 'previous' }, 60 * 1000);
    return data;
  } catch (error) {
    console.error('❌ Error al obtener cuento anterior:', error);
    throw error;
  }
};

/**
 * Obtiene el cuento siguiente basado en fecha de publicación
 * @param {string} currentDocumentId - El documentId del cuento actual
 * @returns {Promise} - Promise con el cuento siguiente
 */
export const getCuentoSiguiente = async (currentDocumentId) => {
  try {
    // Cache corto para navegación (1 minuto)
    const data = await cachedRequest(`/cuentos/adjacent/${currentDocumentId}`, { direction: 'next' }, 60 * 1000);
    return data;
  } catch (error) {
    console.error('❌ Error al obtener cuento siguiente:', error);
    throw error;
  }
};

/**
 * Obtiene cuentos adyacentes (anterior y siguiente) basados en fecha de publicación
 * @param {string} currentDocumentId - El documentId del cuento actual
 * @returns {Promise} - Promise con los cuentos adyacentes
 */
export const getCuentosAdyacentes = async (currentDocumentId) => {
  try {
    // Cache corto para navegación (1 minuto)
    const data = await cachedRequest(`/cuentos/adjacent/${currentDocumentId}`, {}, 60 * 1000);
    return data;
  } catch (error) {
    console.error('❌ Error al obtener cuentos adyacentes:', error);
    throw error;
  }
};

/**
 * Obtiene cuentos anterior y siguiente usando el nuevo endpoint
 * @param {string} documentId - El documentId del cuento actual
 * @returns {Promise} - Promise con actual, anterior y siguiente
 */
export const getCuentosAnteriorSiguiente = async (documentId) => {
  try {
    console.log('🔍 Obteniendo cuentos anterior/siguiente para:', documentId);

    // Cache corto para navegación (1 minuto)
    const data = await cachedRequest(`/cuentos/${documentId}/anterior-siguiente`, {}, 60 * 1000);

    console.log('📚 Cuentos anterior/siguiente obtenidos:', {
      actual: data?.actual?.documentId,
      anterior: data?.anterior?.documentId,
      siguiente: data?.siguiente?.documentId
    });

    return data;
  } catch (error) {
    console.error('❌ Error al obtener cuentos anterior/siguiente:', error);
    throw error;
  }
};

// Funciones de utilidad para el cache
export const cacheUtils = {
  /**
   * Limpia todo el cache
   */
  clearCache: async () => {
    await cuentosCache.clear();
  },

  /**
   * Invalida cache por patrón
   * @param {string} pattern - Patrón para invalidar
   */
  invalidateCache: async (pattern) => {
    await cuentosCache.invalidate(pattern);
  },

  /**
   * Obtiene estadísticas del cache
   * @returns {Object} Estadísticas del cache
   */
  getCacheStats: () => {
    return cuentosCache.getStats();
  },

  /**
   * Configura el TTL por defecto
   * @param {number} ttl - Tiempo de vida en milisegundos
   */
  setDefaultTTL: (ttl) => {
    cuentosCache.defaultTTL = ttl;
  },

  /**
   * Pre-carga cuentos populares en cache
   * @param {Array} documentIds - Array de documentIds a pre-cargar
   */
  preloadCuentos: async (documentIds) => {
    console.log('🚀 Pre-cargando cuentos:', documentIds.length);
    const promises = documentIds.map(async (documentId) => {
      try {
        await getCuentoByDocumentId(documentId);
      } catch (error) {
        console.warn('⚠️ Error pre-cargando cuento:', documentId, error);
      }
    });
    await Promise.allSettled(promises);
    console.log('✅ Pre-carga completada');
  },

  /**
   * Obtiene información del tamaño del localStorage
   * @returns {Object} Información del storage
   */
  getStorageInfo: () => {
    try {
      let totalSize = 0;
      let cacheItems = 0;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('cuentos_cache_v1')) {
          const value = localStorage.getItem(key);
          totalSize += (key.length + value.length) * 2;
          cacheItems++;
        }
      }
      
      return {
        totalSize: totalSize,
        totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
        cacheItems,
        limitMB: 50
      };
    } catch (error) {
      console.warn('⚠️ Error getting storage info:', error);
      return null;
    }
  }
}; 